<script lang="ts" setup>
import { Badge } from '~ds/components/ui/badge';
import { Text } from '~ds/components/ui/typography';

export interface KUICoreRegionSelectOption {
  /** Region key, e.g. `USA` / `AUS`. */
  geo: string;
  /** Translated region name, e.g. `United States`. */
  label: string;
  /** Flag glyph shown beside the label, e.g. `🇺🇸`. */
  flag: string;
  isCurrent: boolean;
}

interface Props {
  regions?: KUICoreRegionSelectOption[];
  title?: string;
  description?: string;
  /** Tag shown on the region currently being served (already translated). */
  currentLabel?: string;
  /**
   * Whether the visitor can close the dialog without choosing. The first-visit
   * selector is a deliberate choice, so it locks; reopening from the header or
   * footer stays dismissable.
   */
  dismissible?: boolean;
}

withDefaults(defineProps<Props>(), {
  regions: () => [],
  title: '',
  description: '',
  currentLabel: '',
  dismissible: false,
});

const emit = defineEmits<{
  select: [region: KUICoreRegionSelectOption];
}>();

const open = defineModel<boolean>({ default: false });
</script>

<template>
  <KUICoreDialog
    v-model="open"
    :heading="title"
    :description="description"
    alignment="center"
    content-width="fit"
    has-overlay
    :prevent-close="!dismissible"
    :show-close-button="dismissible"
    data-testid="region-select-dialog"
  >
    <KUICoreListRenderer class="flex w-full flex-col gap-3">
      <KUICoreClickable
        v-for="region in regions"
        :key="region.geo"
        type="button"
        class="border-border hover:bg-secondary-subtle flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors"
        :data-testid="`region-option-${region.geo}`"
        @click="emit('select', region)"
      >
        <Text
          as="span"
          aria-hidden="true"
          class="text-3xl leading-none"
        >
          {{ region.flag }}
        </Text>
        <Text
          size="lg"
          as="span"
          weight="medium"
          class="text-foreground grow"
        >
          {{ region.label }}
        </Text>
        <Badge v-if="region.isCurrent && currentLabel">
          {{ currentLabel }}
        </Badge>
      </KUICoreClickable>
    </KUICoreListRenderer>
  </KUICoreDialog>
</template>
